import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Check, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="flex items-center gap-0.5" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} className={`${size} ${i < value ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
    ))}
  </span>
);

// Modale « Laisser un avis » (réutilisable : section avis, FAQ, pages de suivi…)
export function ReviewModal({ onClose }) {
  const [form, setForm] = useState({ rating: 5, text: "", first_name: "", postal_code: "", city: "" });
  const [cityLoading, setCityLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Détection de la ville depuis le code postal (geo.api.gouv.fr, sans clé)
  useEffect(() => {
    const cp = (form.postal_code || "").trim();
    if (!/^\d{5}$/.test(cp)) return undefined;
    let cancelled = false;
    setCityLoading(true);
    const t = setTimeout(() => {
      fetch(`https://geo.api.gouv.fr/communes?codePostal=${cp}&fields=nom&limit=1`)
        .then((r) => r.json())
        .then((arr) => { if (!cancelled) setForm((f) => ({ ...f, city: (arr && arr[0]?.nom) || "" })); })
        .catch(() => { if (!cancelled) setForm((f) => ({ ...f, city: "" })); })
        .finally(() => { if (!cancelled) setCityLoading(false); });
    }, 350);
    return () => { cancelled = true; clearTimeout(t); setCityLoading(false); };
  }, [form.postal_code]);

  const submit = async () => {
    const text = form.text.trim();
    if (text.length < 5) { toast.error("Ton avis est trop court (5 caractères minimum)."); return; }
    if (!form.first_name.trim()) { toast.error("Indique ton prénom."); return; }
    if (!/^\d{5}$/.test(form.postal_code)) { toast.error("Indique ton code postal (5 chiffres)."); return; }
    setSubmitting(true);
    try {
      await api.post("/public/reviews", {
        rating: form.rating, text,
        first_name: form.first_name.trim(),
        postal_code: form.postal_code, city: form.city.trim(),
      });
      onClose();
      toast.success("Merci ! Ton avis a bien été envoyé. Il sera publié après validation.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "L'envoi a échoué, réessaie.");
    } finally { setSubmitting(false); }
  };

  return (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-4 sm:items-center" data-testid="review-modal" onClick={onClose}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-extrabold tracking-tight text-ink">Laisser un avis</h3>
              <button type="button" aria-label="Fermer" onClick={onClose} className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-3 text-xs font-semibold text-neutral-500">Ta note</p>
            <div className="mt-1 flex items-center gap-1" data-testid="review-stars-input">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setForm((f) => ({ ...f, rating: n }))} className="p-0.5" aria-label={`${n} étoiles`}>
                  <Star className={`h-7 w-7 ${n <= form.rating ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
                </button>
              ))}
            </div>
            <label className="mt-4 block text-xs font-semibold text-neutral-500">Ton avis</label>
            <textarea data-testid="review-text-input" value={form.text} onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} rows={3} maxLength={500} placeholder="Raconte ton expérience en quelques mots…" className="mt-1 w-full resize-none rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand" />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-semibold text-neutral-500">Prénom</span>
                <input data-testid="review-firstname-input" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} placeholder="Ton prénom" className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-neutral-500">Code postal</span>
                <input data-testid="review-postal-input" value={form.postal_code} inputMode="numeric" maxLength={5} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value.replace(/\D/g, "") }))} placeholder="Ex : 69001" className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand" />
                <span className="mt-1 block min-h-[1rem] text-xs font-medium text-neutral-500" data-testid="review-detected-city">
                  {cityLoading ? "Détection de la ville…" : (form.city ? form.city : (/^\d{5}$/.test(form.postal_code) ? "Ville introuvable" : ""))}
                </span>
              </label>
            </div>
            <button type="button" data-testid="submit-review-btn" disabled={submitting} onClick={submit} className="mt-5 w-full rounded-xl bg-brand py-3 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60">
              {submitting ? "Envoi…" : "Publier mon avis"}
            </button>
          </div>
        </div>
  );
}

// Bouton autonome « Laisser un avis » (ouvre la modale)
export function LeaveReviewButton({ className = "", label = "Laisser un avis", testId = "open-review-modal-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid={testId} onClick={() => setOpen(true)} className={className}>{label}</button>
      {open && <ReviewModal onClose={() => setOpen(false)} />}
    </>
  );
}

// Statistiques publiques des avis (note moyenne + nombre)
export function useReviewStats() {
  const [stats, setStats] = useState({ count: 0, average: 0 });
  useEffect(() => {
    api.get("/public/reviews/stats").then(({ data }) => { if (data) setStats(data); }).catch(() => {});
  }, []);
  return stats;
}

// Section avis : MASQUÉE tant qu'il n'y a aucun avis publié (pas de fausse preuve sociale).
export default function ReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ count: 0, average: 0 });

  useEffect(() => {
    api.get("/public/reviews").then(({ data }) => { if (Array.isArray(data)) setReviews(data); }).catch(() => {});
    api.get("/public/reviews/stats").then(({ data }) => { if (data) setStats(data); }).catch(() => {});
  }, []);

  const list = reviews.slice(0, 6);
  if (!list.length) return null;

  return (
    <section className="bg-white px-6 py-16 sm:px-12 lg:px-20" data-testid="reviews-section" id="avis">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand">Avis élèves</p>
            <h2 className="mt-2 text-3xl font-black tracking-tight text-ink sm:text-4xl">Ils ont trouvé leur place avec PlanPermis</h2>
            {stats.count > 0 && (
              <div className="mt-3 flex items-center justify-center gap-2 sm:justify-start" data-testid="reviews-summary">
                <Stars value={Math.round(stats.average)} size="h-5 w-5" />
                <span className="text-lg font-black text-ink">{Number(stats.average).toFixed(1)}/5</span>
                <span className="text-sm text-neutral-500">· {stats.count} avis vérifiés</span>
              </div>
            )}
          </div>
          <LeaveReviewButton className="shrink-0 rounded-xl border border-brand px-5 py-3 text-sm font-bold text-brand hover:bg-brand/5" />
        </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((r, i) => (
              <figure key={i} className="flex flex-col rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="review-card">
                <div className="flex items-center gap-2">
                  <Stars value={r.rating} />
                  <span className="text-xs font-bold text-neutral-500">{r.rating}/5</span>
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-neutral-700">«&nbsp;{r.text}&nbsp;»</blockquote>
                <figcaption className="mt-4 flex items-center gap-2 border-t border-neutral-100 pt-4">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-brand/10 text-xs font-bold text-brand">
                    {(r.first_name || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="text-xs font-semibold text-neutral-600">{r.first_name}{r.city ? ` · ${r.city}` : ""}</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><Check className="h-3 w-3" /> Vérifié</span>
                </figcaption>
              </figure>
            ))}
          </div>
      </div>
    </section>
  );
}
